/* Common Imports */

import { styled } from "@mui/system";

/* Component Imports */

import { Skeleton, useTheme } from "@mui/material";

/* Styled Components */

const PropertyCardContainer = styled("div", {
	shouldForwardProp: (prop) => prop !== "source",
})<{ source: string }>(({ theme, source }) => ({
	width: "100%",
	borderRadius: "0.75rem",
	boxShadow: "0px 4px 18px 0px rgba(0, 0, 0, 0.05)",
	height: "100%",
	[theme.breakpoints.up("md1190")]: {
		width: "49%",
	},
	/* 4k breakpoint 3840p */
	[theme.breakpoints.up(theme.breakpoints.values.xl + 1920)]: {
		width: "32.25%",
	},
}));

const PropertyCard = styled("div", {
	shouldForwardProp: (prop) => prop !== "source" && prop !== "isEditor",
})<{ source: string; isEditor: boolean }>(({ theme, source, isEditor }) => ({
	background: theme.palette.background.paper,
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	borderRadius: "0.75rem",
	objectFit: "contain",
	width: "100%",
	flexShrink: 1,
	flexGrow: 1,
	gap: "0.75rem",
	height: "100%",
	scrollSnapAlign: "start",
}));

const CustomSkeleton = styled(Skeleton)(({ theme }) => ({
	borderRadius: "0.75rem",
	width: "80%",
	/* XS breakpoint - 280.05 */
	[theme.breakpoints.up("xs")]: {
		height: "8rem",
	},
	/* iphone 5 - 320 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 39)]: {
		height: "5rem",
	},
	/* galaxy S8+ - 360 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 78)]: {
		height: "5.5rem",
	},
	/* iphone 12 pro and pixel - 390 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 108.95)]: {
		height: "5.75rem",
	},
	/* iphone XR - 414 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 130)]: {
		height: "6rem",
	},
	/* iphone 14 pro max - 430 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 149)]: {
		height: "6.25rem",
	},
	/* 457 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 176)]: {
		height: "6.75rem",
	},
	/* 485 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 204)]: {
		height: "7.25rem",
	},
	/* 512 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 231)]: {
		height: "7.5rem",
	},
	/* 540 */
	[theme.breakpoints.up("xsPlus")]: {
		height: "7.75rem",
	},
	/* 570  ~~3 cards start~~ */
	[theme.breakpoints.up(theme.breakpoints.values.xsPlus + 30)]: {
		height: "5.5rem",
	},
	/* 600  ~~2 cards start~~ */
	[theme.breakpoints.up("sm")]: {
		height: "7.75rem",
	},
	/* 642 ~~3 cards start~~ */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 42)]: {
		height: "6rem",
	},
	/* 684 */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 84)]: {
		height: "6.25rem",
	},
	/* ipad Mini - 768 */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 167)]: {
		height: "7rem",
	},
	/* ipad Air - 820 */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 219)]: {
		height: "8rem",
	},
	/* 860 */
	[theme.breakpoints.up("sm860")]: {
		height: "8.25rem",
	},
	/* 910 */
	[theme.breakpoints.up("sm910")]: {
		height: "8.5rem",
	},
	/* 960 */
	[theme.breakpoints.up("md")]: {
		height: "8.75rem",
	},
	/* iPadPro - 1024 */
	[theme.breakpoints.up(theme.breakpoints.values.md + 64)]: {
		height: "9.25rem",
	},
	/* 1107 */
	[theme.breakpoints.up(theme.breakpoints.values.md + 147)]: {
		height: "9.5rem",
	},
	/* 1190 ~~Desktop Layout Start~~ */
	[theme.breakpoints.up("md1190")]: {
		height: "8rem",
	},
	/* 720p breakpoint */
	[theme.breakpoints.up("lg")]: {
		height: "8.5rem",
	},
	/* 1080p 125% breakpoint */
	[theme.breakpoints.up(theme.breakpoints.values.lg + 150)]: {
		height: "9rem",
	},
	/* 4k breakpoint 3840p */
	[theme.breakpoints.up(theme.breakpoints.values.xl + 1920)]: {
		height: "9rem",
	},
}));

const PropertyCardContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	height: "100%",
	cursor: "pointer",
	padding: "0rem 0.75rem 0rem 0.75rem",
}));

const PropertyCardInfoContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	padding: "0rem 0rem 0rem 0.5rem",
	width: "100%",
	height: "100%",
	cursor: "pointer",
}));

const PropertyCardStatsContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	padding: "0rem 0.75rem 0rem 0.75rem",
	width: "100%",
	gap: "0.75rem",
	cursor: "pointer",
}));

const TitlePriceSkeleton = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	gap: "0.25rem",
	margin: "0.25rem 0rem 0.5rem 0rem",
}));

const InfoContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	margin: "0.45rem 0rem 0rem 0rem",
	width: "100%",
	cursor: "pointer",
	gap: "0.5rem",
	/* ipad Mini - 768 */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 167)]: {
		gap: "0.65rem",
	},
	/* 860 */
	[theme.breakpoints.up("sm860")]: {
		gap: "0.75rem",
	},
	/* 910 */
	[theme.breakpoints.up("sm910")]: {
		gap: "0.85rem",
	},
	/* 1190 ~~Desktop Layout Start~~ */
	[theme.breakpoints.up("md1190")]: {
		gap: "0.75rem",
	},
}));

const RowContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
}));

const PropertyLoadingCardDashboardEditorView = ({
	source,
	isEditor,
	show_only_listing_details,
}: {
	source?: string;
	isEditor?: boolean | undefined;
	show_only_listing_details: boolean;
}) => {
	const theme = useTheme();

	return (
		<PropertyCardContainer
			source={source ? source : ""}
			sx={{
				boxShadow: !show_only_listing_details ? "none" : "none",
				border: !show_only_listing_details ? "1px solid #E0E0E0" : "none",
				borderRadius: "1rem",
				width: "100%",
				[theme.breakpoints.up("lgPlus")]: {
					width: !show_only_listing_details ? "49%" : "100%",
				},
			}}
		>
			<PropertyCard
				source={source ? source : ""}
				isEditor={isEditor !== undefined ? isEditor : false}
				sx={{
					padding: "0rem",
				}}
			>
				{/* Image + Info row */}

				<PropertyCardContentContainer
					sx={{
						padding: "1rem",
						gap: "1rem",
						alignItems: "flex-start",
					}}
				>
					{/* Image */}

					<CustomSkeleton
						animation="wave"
						variant="rectangular"
					/>

					{/* Property Details */}

					<PropertyCardInfoContainer
						sx={{
							padding: "0rem",
							gap: "0.5rem",
						}}
					>
						{/* Title */}

						<Skeleton
							animation="wave"
							variant="rectangular"
							width="80%"
							height="1.25rem"
							style={{ borderRadius: "0.25rem" }}
						/>

						{/* Location | Price | Config row */}

						<RowContainer
							sx={{
								gap: "0.75rem",
								justifyContent: "flex-start",
								marginTop: "0.25rem",
							}}
						>
							<Skeleton
								animation="wave"
								variant="rectangular"
								width="30%"
								height="0.75rem"
								style={{ borderRadius: "0.25rem" }}
							/>
							<Skeleton
								animation="wave"
								variant="rectangular"
								width="25%"
								height="0.75rem"
								style={{ borderRadius: "0.25rem" }}
							/>
							<Skeleton
								animation="wave"
								variant="rectangular"
								width="20%"
								height="0.75rem"
								style={{ borderRadius: "0.25rem" }}
							/>
						</RowContainer>

						{/* Stats chip (Leads | Views) */}

						{!show_only_listing_details ? (
							<Skeleton
								animation="wave"
								variant="rectangular"
								width="40%"
								height="2rem"
								style={{ borderRadius: "0.25rem", marginTop: "0.75rem" }}
							/>
						) : null}
					</PropertyCardInfoContainer>
				</PropertyCardContentContainer>

				{/* Bottom action bar */}

				{!show_only_listing_details ? (
					<>
						<Skeleton
							animation="wave"
							variant="rectangular"
							width="100%"
							height="1px"
						/>
						<RowContainer
							sx={{
								width: "100%",
								padding: "0.75rem 1rem",
							}}
						>
							<RowContainer
								sx={{
									gap: "0.5rem",
									justifyContent: "flex-start",
									width: "auto",
								}}
							>
								<Skeleton
									animation="wave"
									variant="rectangular"
									width="6rem"
									height="1.75rem"
									style={{ borderRadius: "0.25rem" }}
								/>
								<Skeleton
									animation="wave"
									variant="rectangular"
									width="5rem"
									height="1.75rem"
									style={{ borderRadius: "0.25rem" }}
								/>
							</RowContainer>
							<Skeleton
								animation="wave"
								variant="rectangular"
								width="8rem"
								height="0.75rem"
								style={{ borderRadius: "0.25rem" }}
							/>
						</RowContainer>
					</>
				) : null}
			</PropertyCard>
		</PropertyCardContainer>
	);
};

export default PropertyLoadingCardDashboardEditorView;
